class SgfUtil {
  static alphabet = "abcdefghijklmnopqrstuvwxyz";
  static gtpAlphabet = "ABCDEFGHJKLMNOPQRSTUVWXYZ";
  static gtpVertexPattern = /^[A-Z]\d{1,2}$/
  static sgfVertexPattern = /^[a-t][a-t](:[a-t][a-t])*$/
  static compressedPointsPattern = /^[a-t][a-t]:[a-t][a-t]$/

  /**
   * Converts index(0-360) to GTP vertex A1-T19.
   * 
   * @param {number} index
   * @param {number} boardSize
   */
  static indexToGtpVertex(index, boardSize) {
    if (!boardSize) {
      throw new Error("No boardSize parameter");
    } else if (boardSize < 1 || boardSize > 19) {
      throw new RangeError("boardSize must be 1-19.");
    } else if (index < 0 || index > boardSize ** 2) {
      throw new RangeError("index is out of range: " + index);
    }
    if (index === boardSize ** 2) { // pass
      return "";
    }
    return SgfUtil.gtpAlphabet[index % boardSize] + 
      (boardSize - Math.floor(index / boardSize));
  }

  /**
   * Converts index(0-360) to sgf vertex aa-ss.
   * 
   * @param {number} index
   * @param {number} boardSize
   */
  static indexToSgfVertex(index, boardSize) {
    if (!boardSize) {
      throw new Error("No boardSize parameter");
    } else if (boardSize < 1 || boardSize > 19) {
      throw new RangeError("boardSize must be 1-19.");
    } else if (index < 0 || index > boardSize ** 2) {
      throw new RangeError("index is out of range: " + index);
    }
    if (index === boardSize ** 2) { // pass
      return "";
    }
    return SgfUtil.alphabet[index % boardSize] + 
      SgfUtil.alphabet[Math.floor(index / boardSize)];
  }

  /**
   * Converts index(0-360) to vertex[1-19][1-19].
   * 
   * @param {number} index
   * @param {number} boardSize
   */
  static indexToVertex(index, boardSize) {
    if (!boardSize) {
      throw new Error("No boardSize parameter");
    } else if (boardSize < 1 || boardSize > 19) {
      throw new RangeError("boardSize must be 1-19.");
    } else if (index < 0 || index >= boardSize ** 2) {
      throw new RangeError("index is out of range: " + index);
    }
    return [index % boardSize + 1, Math.floor(index / boardSize) + 1]
  }

  /**
   *  Converts GTP vertex A1-T19 to index(0-360).
   */
  static gtpVertexToIndex(gtpVertex, boardSize) {
    if (!boardSize) {
      throw new Error("No boardSize parameter");
    } else if (boardSize < 1 || boardSize > 19) {
      throw new RangeError("boardSize must be 1-19.");
    } else if (!gtpVertex || !gtpVertex.match(SgfUtil.gtpVertexPattern)) {
      throw new Error("Not valid vertex: ", gtpVertex);
    }
    const x = gtpVertex[0];
    const y = gtpVertex.substring(1);
    return SgfUtil.gtpAlphabet.indexOf(x) + boardSize * (boardSize - y);
  }

  /**
   *  Converts GTP vertex A1-T19 to vertex[1-19][1-19].
   */
  static gtpVertexToVertex(gtpVertex, boardSize) {
    if (!boardSize) {
      throw new Error("No boardSize parameter");
    } else if (boardSize < 1 || boardSize > 19) {
      throw new RangeError("boardSize must be 1-19.");
    } else if (!gtpVertex || !gtpVertex.match(SgfUtil.gtpVertexPattern)) {
      throw new Error("Not valid vertex: ", gtpVertex);
    }
    const x = gtpVertex[0];
    const y = gtpVertex.substring(1);
    return [SgfUtil.gtpAlphabet.indexOf(x) + 1, boardSize - y + 1]
  }

  /**
   * Converts sgf vertex aa-ss to index(0-360).
   * 
   * @param {*} sgfVertex 
   * @param {number} boardSize
   */
  static sgfVertexToIndex(sgfVertex, boardSize) {
    if (!boardSize) {
      throw new Error("No boardSize parameter");
    } else if (boardSize < 1 || boardSize > 19) {
      throw new RangeError("boardSize must be 1-19.");
    } else if (!sgfVertex || !sgfVertex.match(SgfUtil.sgfVertexPattern)) {
      throw new Error("Not valid vertex: ", sgfVertex);      
    }
    const x = SgfUtil.alphabet.indexOf(sgfVertex[0]);
    const y = SgfUtil.alphabet.indexOf(sgfVertex[1]);
    return x + y * boardSize;
  }

  /**
   *  Converts sgf vertex aa-ss to vertex[1-19][1-19].
   */
  static sgfVertexToVertex(sgfVertex) {    
    if (!sgfVertex || !sgfVertex.match(SgfUtil.sgfVertexPattern)) {
      throw new Error("Not valid sgfVertex: ", sgfVertex);
    }
    const x = sgfVertex[0];
    const y = sgfVertex[1];
    return [SgfUtil.alphabet.indexOf(x) + 1, SgfUtil.alphabet.indexOf(y) + 1]
  }

  static isCompressedPoints(sgfVertex) {
    return sgfVertex && sgfVertex.match(SgfUtil.compressedPointsPattern);
  }

  static uncompressPoints(sgfVertex) {
    if (!sgfVertex || !sgfVertex.match(SgfUtil.compressedPointsPattern)) {
      throw new Error("Not a compressed points: ", sgfVertex);
    }
    const splitted = sgfVertex.split(':');
    const [x1, y1] = SgfUtil.sgfVertexToVertex(splitted[0]);
    const [x2, y2] = SgfUtil.sgfVertexToVertex(splitted[1]);
    const sgfVertices = [];
    for (let x = Math.min(x1, x2); x <= Math.max(x1, x2); x++) {
      for (let y = Math.min(y1, y2); y <= Math.max(y1, y2); y++) {
        sgfVertices.push(SgfUtil.vertexToSgfVertex([x, y]));
      }
    }
    return sgfVertices;
  }

  /**
   *  Converts vertex[1-19][1-19] to sgf vertex aa-ss.
   */
  static vertexToSgfVertex(vertex) {
    if (!vertex || vertex.length !== 2) {
      throw new Error("Not valid vertex: ", vertex);
    }
    return SgfUtil.alphabet[vertex[0] - 1] + SgfUtil.alphabet[vertex[1] - 1];
  }

  /**
   *  Converts vertex[1-19][1-19] to gtp vertex A1-T19.
   */
  static vertexToGtpVertex(vertex, boardSize) {
    if (!boardSize) {
      throw new Error("No boardSize parameter");
    } else if (boardSize < 1 || boardSize > 19) {
      throw new RangeError("boardSize must be 1-19.");
    } else if (!vertex || vertex.length !== 2) {
      throw new Error("Not valid vertex: ", vertex);
    }
    return SgfUtil.gtpAlphabet[vertex[0] - 1] + (boardSize - vertex[1] + 1);
  }

  /**
   * Convert vertex[1-19][1-19] to index(0-360).
   * 
   * @param {*} vertex 
   * @param {*} boardSize 
   */
  static vertexToIndex(vertex, boardSize) {
    if (!boardSize) {
      throw new Error("No boardSize parameter");
    } else if (boardSize < 1 || boardSize > 19) {
      throw new RangeError("boardSize must be 1-19.");
    } else if (!vertex || vertex.length !== 2) {
      throw new Error("Not valid vertex: ", vertex);
    }
    return (vertex[1] - 1) * boardSize + (vertex[0] - 1);
  }
}

export default SgfUtil;